// apps/web/src/panels/ChatPanel.tsx
import { useRef, useState, useEffect } from "react"
import { Panel } from "../Panel"
import { useChatStream } from "../useChatStream"
import { useChat } from "../store"
import { inverseAction } from "../dashboard-actions"

export function ChatPanel() {
  const { messages, streaming, send, cancel, notConfigured } = useChatStream()
  const undoLast = () => {
    const st = useChat.getState()
    const last = st.actionsLog[st.actionsLog.length - 1]
    if (!last) return
    const inv = inverseAction(st, last)
    if (inv) st.applyAction(inv)
  }
  const [draft, setDraft] = useState("")
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Scroll only the conversation, never pull the whole dashboard down to chat.
    const conversation = endRef.current?.parentElement
    if (conversation && messages.length) conversation.scrollTo({ top: conversation.scrollHeight, behavior: "smooth" })
  }, [messages])

  function submit() {
    const text = draft.trim()
    if (!text || streaming) return
    setDraft("")
    send(text)
  }

  return (
    <Panel
      title="Cockpit chat"
      live={streaming}
      helpText="Ask questions, run scenarios, reshape the dashboard."
      titleAccessory={
        <button
          type="button"
          onClick={undoLast}
          className="text-[10px] text-[color:var(--text-dim)] hover:text-[color:var(--text)]"
          title="Undo last dashboard action"
        >
          ↶ undo
        </button>
      }
    >
      <div className="flex flex-col h-full">
        <div className="flex-1 min-h-0 overflow-y-auto px-3 py-2 space-y-2 text-[12px]">
          {messages.length === 0 && (
            <div className="text-[color:var(--text-dim)] italic">
              Try: "forecast brent for 14 days in a new panel" or "what if Suez rates spike 20%?"
            </div>
          )}
          {messages.map((m) =>
            m.role === "user" ? (
              <div key={m.id} className="flex justify-end">
                <div className="max-w-[85%] rounded px-2 py-1 bg-[color:var(--accent)]/15 text-[color:var(--text)]">
                  {m.content}
                </div>
              </div>
            ) : (
              <div key={m.id} className="space-y-1">
                {m.toolCalls.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {m.toolCalls.map((c, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-[color:var(--surface)] border border-[color:var(--border)] text-[color:var(--text-dim)]"
                        title={JSON.stringify(c.args)}
                      >
                        🔧 {c.tool}
                        {c.status === "running" && <span className="animate-pulse">…</span>}
                      </span>
                    ))}
                  </div>
                )}
                {m.content && (
                  <div className="whitespace-pre-wrap text-[color:var(--text)]">{m.content}</div>
                )}
              </div>
            ),
          )}
          <div ref={endRef} />
        </div>
        {notConfigured && (
          <div className="mx-3 mb-2 rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200 leading-relaxed">
            <div className="font-bold uppercase tracking-wider text-amber-300 mb-1">
              AI not configured
            </div>
            The chat assistant needs a Gemini API key. Set{" "}
            <code className="text-amber-100">GEMINI_API_KEY</code> in the backend environment
            (root <code className="text-amber-100">.env</code>) and restart the backend to
            enable chat. Dashboard panels keep working without it.
          </div>
        )}
        <div className="px-3 py-2 border-t border-[color:var(--border)] flex gap-2">
          <textarea
            className="flex-1 bg-[color:var(--surface)] border border-[color:var(--border)] rounded px-2 py-1 text-[12px] resize-none"
            rows={2}
            placeholder={streaming ? "streaming…" : "ask the cockpit"}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault()
                submit()
              }
            }}
            disabled={streaming}
          />
          {streaming ? (
            <button
              type="button"
              onClick={cancel}
              className="btn-pill text-[11px] px-3 bg-red-500/20 text-red-400"
            >
              stop
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={!draft.trim()}
              className="btn-pill text-[11px] px-3 bg-[color:var(--accent)]/25 text-[color:var(--accent)] disabled:opacity-40"
            >
              send
            </button>
          )}
        </div>
      </div>
    </Panel>
  )
}
